import { z } from 'zod';
import { fileListItemSchema } from '../types/file-service';
import axios from 'axios';

type AcceptedMediaType = {
    value: string;
    type: 'extension' | 'mime';
};

export const acceptedMediaTypes: AcceptedMediaType[] = [
    {
        type: 'mime',
        value: 'image/jpeg',
    },
    {
        type: 'mime',
        value: 'image/png',
    },
    {
        type: 'mime',
        value: 'image/gif',
    },
    {
        type: 'mime',
        value: 'image/webp',
    },
    {
        type: 'mime',
        value: 'image/svg+xml',
    },
    {
        type: 'mime',
        value: 'video/mp4',
    },
    {
        type: 'mime',
        value: 'video/webm',
    },
    {
        type: 'mime',
        value: 'video/ogg',
    },
    {
        type: 'mime',
        value: 'application/pdf',
    },
    {
        type: 'extension',
        value: '.glb',
    },
];

const uploadedInternalMediaObjectSchema = z.object({
    type: z.literal('internal'),
    file: fileListItemSchema,
});
export type UploadedInternalMediaObject = z.infer<typeof uploadedInternalMediaObjectSchema>;

const externalMediaObjectSchema = z.object({
    type: z.literal('external'),
    url: z.string(),
    id: z.string(),
});
export type ExternalMediaObject = z.infer<typeof externalMediaObjectSchema>;

const pendingMediaObjectReferenceSchema = z.object({
    type: z.literal('pending'),
    id: z.string(),
});

export type PendingMediaObjectReference = z.infer<typeof pendingMediaObjectReferenceSchema>;

export const mediaObjectSchema = z.discriminatedUnion('type', [
    uploadedInternalMediaObjectSchema,
    externalMediaObjectSchema,
    pendingMediaObjectReferenceSchema, // this type is part of the union purely for convenience in UI. it should never end up in the db.
]);

export type MediaObject = z.infer<typeof mediaObjectSchema>;

export function removeFileExtension (filename: string): string {
    return filename.replace(/\.[^/.]+$/ui, '');
}

export const is3DModelFile = async (src: ArrayBuffer | string) => {
    const buffer = typeof src === 'string' ? null : src;
    const url = typeof src === 'string' ? src : null;
    if (buffer) {
        const view = new DataView(buffer);
        const value = view.getUint32(0, true);
        return value === 0x46546C67;
    } else if (url) {
        const preamble = await axios.get(url, {
            responseType: 'arraybuffer',
            headers: {
                Range: 'bytes=0-3', // minimum bytes required to check the file type
            },
            params: {
                'cache-bypass': Date.now(), // prevent caching
            },
        });
        const buffer = preamble.data;
        if (!buffer || !(buffer instanceof ArrayBuffer)) {
            return new Error('Response data is not an ArrayBuffer');
        }
        try {
            const textval = new TextDecoder('utf-8').decode(buffer);
            if (textval.startsWith('glTF')) {
                return true;
            } else {
                return false;
            }
        } catch {
            throw new Error('Failed to decode 3D model file preamble');
        }
    }
    return new Error('Invalid input: must be a File or a URL string');
};


export function getUrlForMedia (media: MediaObject) {
    if (media.type === 'external') {
        return media.url;
    } else if (media.type === 'internal') {
        return `/file/config/${media.file.key}`;
    } else {
        throw new Error(`Pending media found in The DB! Please update this configuration: ${JSON.stringify(media)}`);
    }
}


export function getMetaForMedia (media: MediaObject) {
    if (media.type === 'external') {
        return {};
    } else if (media.type === 'internal') {
        return media.file.meta || {};
    } else {
        throw new Error(`Pending media found in The DB! Please update this configuration: ${JSON.stringify(media)}`);
    }
}
